"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const disciplines = [{
  title: "National SEO",
  linkTo: "/national-seo",
  body: <>
        As an independent <Link to="/FreelanceSeoConsultantPage" className="text-primary underline underline-offset-4 hover:no-underline transition-colors">freelance SEO consultant</Link>, I build authority across the UK market, ensuring your brand ranks for high-intent keywords. My approach focuses on sustainable growth, combining technical precision with content that resonates with your audience and search engines alike.
      </>,
  linkLabel: "Explore National SEO",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M4 26 C 10 14, 18 14, 22 26 S 34 38, 44 22" stroke="hsl(340, 75%, 65%)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
}, {
  title: "International SEO",
  linkTo: "/international-seo",
  body: "Expanding into new territories requires more than translation. I manage hreflang implementation, geo-targeting, and cross-border search strategy to ensure your site performs optimally in every target market, driving global visibility.",
  linkLabel: "Explore International SEO",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M8 10 L 38 10 L 40 40 L 12 42 L 6 34 Z M 6 34 L 14 32 L 12 42" stroke="hsl(340, 75%, 65%)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
}, {
  title: "Local SEO",
  linkTo: "/local-seo",
  body: "I help businesses dominate local search results. From Google Business Profile optimisation to local citation management and location-specific content, I ensure your business is the first choice for customers in your immediate area.",
  linkLabel: "Explore Local SEO",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M14 24 a 8 8 0 1 1 16 0 a 8 8 0 1 1 -16 0 M 22 24 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0" stroke="hsl(340, 75%, 65%)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
}, {
  title: "B2B SEO & Leads",
  linkTo: "/b2b-seo-lead-generation",
  body: "I focus on high-value B2B search strategies that generate qualified leads. By targeting decision-maker intent and optimising for complex sales cycles, I turn your website into a consistent engine for business growth.",
  linkLabel: "Explore B2B SEO",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M6 42 L 42 6" stroke="hsl(340, 75%, 65%)" strokeWidth="5" strokeLinecap="round" />
      </svg>
}, {
  title: "Monthly SEO Services",
  linkTo: "/monthly-seo-services",
  body: "I manage your SEO on an ongoing basis, handling everything from strategy and technical optimizations to content and link building. With monthly reporting and continuous refinement, I keep your organic traffic growing and your search visibility strong.",
  linkLabel: "Explore Monthly SEO Services",
  glyph: <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" aria-hidden="true">
        <path className="marker-path" d="M8 12 H40 V40 H8 Z M8 20 H40 M16 8 V16 M32 8 V16" stroke="hsl(340, 75%, 65%)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
}];
export default function Services() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const paths = el.querySelectorAll<SVGPathElement>(".marker-path");
      paths.forEach(p => {
        const len = p.getTotalLength();
        p.style.strokeDasharray = `${len}`;
        p.style.strokeDashoffset = `${len}`;
      });
      gsap.set(".discipline-card", {
        y: 24
      });
      gsap.to(".discipline-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        },
        onComplete: () => {
          gsap.to(".marker-path", {
            strokeDashoffset: 0,
            duration: 0.7,
            ease: "power2.out",
            stagger: 0.12
          });
        }
      });
      const refresh = () => ScrollTrigger.refresh();
      requestAnimationFrame(refresh);
      if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
        once: true
      });
      document.fonts?.ready.then(refresh);
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".discipline-card", {
        opacity: 1,
        y: 0
      });
      const paths = el.querySelectorAll<SVGPathElement>(".marker-path");
      paths.forEach(p => {
        p.style.strokeDasharray = "none";
        p.style.strokeDashoffset = "0";
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="30724" id="specialist-seo-services" ref={container} data-nav="light" className="bg-background border-t border-border py-32 md:py-44">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-20 md:mb-28">
          <div className="md:col-span-3">
            <span className="inline-block font-sans text-[15px] font-semibold uppercase leading-none" style={{
            letterSpacing: "0.08em",
            color: "var(--muted-foreground)"
          }}>
              MY SERVICES
            </span>
          </div>
          <div className="md:col-span-9">
            <h1 className="font-serif text-foreground" style={{
            fontSize: "clamp(29px, 2.8vw, 42px)",
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
            fontWeight: 700
          }}>
              Tailored SEO strategies for <em className="italic">sustainable</em> growth.
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {disciplines.map((d, i) => <article key={i} data-index={i} className="discipline-card group relative flex flex-col p-8 md:p-12 transition-transform duration-[250ms] ease-out hover:-translate-y-0.5" style={{
          opacity: 0,
          backgroundColor: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          minHeight: "360px"
        }}>
              <div className="mb-8">{d.glyph}</div>

              <h3 className="font-serif text-foreground mb-5" style={{
            fontSize: "clamp(23px, 1.75vw, 26px)",
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            fontWeight: 600
          }}>
                {d.title}
              </h3>

              <p className="font-sans mb-8" style={{
            fontSize: "18px",
            lineHeight: 1.6,
            color: "var(--foreground)"
          }}>
                {d.body}
              </p>

              <div className="mt-auto pt-4">
                <Link to={d.linkTo} className="inline-flex items-center gap-2 font-sans text-primary group/link" style={{
              fontSize: "17px",
              fontWeight: 600
            }}>
                  <span className="relative">
                    {d.linkLabel}
                    <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-current transition-[width] duration-300 ease-out group-hover/link:w-full" aria-hidden="true" />
                  </span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 ease-out group-hover/link:translate-x-1" />
                </Link>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}